import React, { useContext, useState } from 'react';
import { IInstructorDetails, IInstructors, ISocialLinks } from '@/interfaces/courseDescription';
import { s3Url } from '@/utils/common/env.constants';
import Image from 'next/image';
import { useRouter } from 'next/router';
import CardDetailsView from './CardDetailsView';
import VideoModal from '../../VideoModal';
import { dataLayer } from '../../../../utils/common/dataLayer.utils';
import CourseDescriptionContext from '../../../../context/CourseDescriptionContext';
import CategoryPageContext from '../../../../context/CategoryPageContext';

type Props = {
  mentor: IInstructors;
  isLinkedInButtonEnabled: boolean;
  learnersPlacedToBeShown: boolean;
  industryExpToBeShown: boolean;
  isSocialIconEnabled: boolean;
};

const MentorCard = ({
  mentor,
  isLinkedInButtonEnabled,
  learnersPlacedToBeShown,
  industryExpToBeShown,
  isSocialIconEnabled,
}: Props) => {
  const [modelOpen, setModelOpen] = useState<boolean>(false);
  const { description, social, name, img, video, instructorDetails } = mentor;
  // Social links may arrive nested under `social` or as top-level keys on the
  // mentor object, so resolve each platform from whichever shape is present.
  const mentorSocial = mentor as IInstructors & ISocialLinks;
  const socialLinks: ISocialLinks = {
    linkedin: social?.linkedin ?? mentorSocial.linkedin,
    youtube: social?.youtube ?? mentorSocial.youtube,
    facebook: social?.facebook ?? mentorSocial.facebook,
    instagram: social?.instagram ?? mentorSocial.instagram,
    twitter: social?.twitter ?? mentorSocial.twitter,
  };
  const { courseDetails } = useContext(CourseDescriptionContext);
  const { categoryDetails } = useContext(CategoryPageContext);
  const router = useRouter();
  const isDashboard = router.pathname.includes('/dashboard');
  const sendDataLayerEvent = (videoURL: string | undefined, section: string) => {
    const dataLayerObj = {
      event: 'video_click',
      section_name: section,
      course_id: courseDetails?._id || categoryDetails?._id || '',
      video_url: videoURL,
    };
    dataLayer(dataLayerObj);
  };
  return (
    <div
      className={` ${
        isDashboard ? '!w-[95%] minWebView:!w-[350px]' : '!w-[93%] medium:!w-[411px]'
      }  bg-white rounded-b-[8px] rounded-t-[8px] h-auto shadow-light shrink-0 flex flex-col`}
    >
      <VideoModal
        title={name || ''}
        modalOpen={modelOpen}
        setModelOpen={setModelOpen}
        videoUrl={video?.link || ''}
        section="mentors"
      />
      <div
        className={`relative ${video && 'cursor-pointer'}`}
        onClick={() => {
          if (video && Object.keys(video).length) {
            setModelOpen(true);
            sendDataLayerEvent(video.link, 'mentors');
          }
        }}
        aria-hidden="true"
      >
        <picture>
          <img
            src={
              img?.source === 'url' || img?.link?.startsWith('https')
                ? `${img?.link}`
                : `${s3Url}/user/profile_pictures/${img?.link}`
            }
            alt={name || ''}
            width={411}
            height={204}
            className=" relative !w-full !h-full rounded-t-[8px]"
          />
        </picture>

        {video && Object.keys(video).length ? (
          <>
            {/* <div className="absolute w-[100%] h-[100%] rounded-t-[8px]  translate-y-[-50%] top-[50%] left-[50%] translate-x-[-50%] bg-black opacity-[0.3]" /> */}
            <div className="flex justify-center items-center rounded-[50%] absolute bottom-0 right-0">
              <Image
                src="/images/campusEdgeCourseDescription/main/playButton.svg"
                alt="play"
                width={32}
                height={32}
                loading="lazy"
              />
            </div>
          </>
        ) : null}
      </div>

      <CardDetailsView
        name={name}
        instructorDetails={instructorDetails as IInstructorDetails}
        description={description as string}
        social={socialLinks}
        isLinkedInButtonEnabled={isLinkedInButtonEnabled}
        learnersPlacedToBeShown={learnersPlacedToBeShown}
        industryExpToBeShown={industryExpToBeShown}
        isSocialIconEnabled={isSocialIconEnabled}
      />
    </div>
  );
};

export default MentorCard;
